"use client";

import Uppy, { Meta, type UppyFile, type UploadResult } from "@uppy/core";
import DashboardModal from "@uppy/react/dashboard-modal";
import { useLocale, useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";
import AwsS3, { type AwsBody } from "@uppy/aws-s3";
import DashboardPlugin from "@uppy/dashboard";
import ImageEditor from "@uppy/image-editor";
import Compressor from "@uppy/compressor";
import { createPortal } from "react-dom";
import { useTheme } from "next-themes";
import { v7 as uuidv7 } from "uuid";

import Spanish from "@uppy/locales/lib/es_ES";
import English from "@uppy/locales/lib/en_US";

const locales = {
  es: Spanish,
  en: English,
};

export type UploadType = "image" | "digital-product";

export type UploaderRestrictions = {
  allowedFileTypes?: string[] | null;
  maxNumberOfFiles?: number;
  maxFileSize?: number;
};

// Uppy styles
import "@uppy/image-editor/css/style.min.css";
import "@uppy/dashboard/css/style.min.css";
import "@uppy/core/css/style.min.css";

async function fetchUploadApiEndpoint<T>(
  endpoint: string,
  data: object,
): Promise<T> {
  const res = await fetch(`/api/multipart-upload/${endpoint}`, {
    method: "POST",
    body: JSON.stringify(data),
    headers: {
      accept: "application/json",
      "Content-Type": "application/json",
    },
  });

  return res.json<T>();
}

type Metadata = {
  aspectRatioApplied?: boolean;
};

export type MultipartFileUploaderResult = UploadResult<Metadata, AwsBody>;
export type OnUploadSuccess = (result: MultipartFileUploaderResult) => void;

export interface MultipartFileUploaderProps {
  onUploadSuccess?: OnUploadSuccess;
  aspectRatio?: number;
  enableImageEditor?: boolean;
  existingUrl?: string;
  uploadType?: UploadType;
  restrictions?: UploaderRestrictions;
  fileNamePrefix?: string;
  children:
    | React.ReactNode
    | ((props: {
        currentUrl?: string;
        openUploadModal: () => void;
        clearFile: () => void;
      }) => React.ReactNode);
}

export function MultipartFileUploader({
  onUploadSuccess,
  aspectRatio,
  enableImageEditor,
  existingUrl,
  uploadType = "image",
  restrictions,
  fileNamePrefix = "upload",
  children,
}: MultipartFileUploaderProps) {
  const theme = useTheme();
  const t = useTranslations("MultipartFileUploader");
  const locale = useLocale();
  const [isOpen, setIsOpen] = useState(false);
  const [currentUrl, setCurrentUrl] = useState<string | undefined>(existingUrl);

  const uppyLocale = locales[locale as keyof typeof locales] || English;

  const shouldUseImageEditor = enableImageEditor ?? Boolean(aspectRatio);

  const uppy = useMemo(() => {
    const instance = new Uppy<Metadata, AwsBody>({
      locale: uppyLocale,
      restrictions,
      onBeforeFileAdded: (file) => ({
        ...file,
        name: `${fileNamePrefix}_${uuidv7()}.${file.extension ?? "bin"}`,
      }),
    }).use(AwsS3<Meta, AwsBody>, {
      getUploadParameters: (file) =>
        fetchUploadApiEndpoint("get-upload-parameters", {
          file,
          contentType: file.type,
          uploadType,
        }),
      createMultipartUpload: (file) =>
        fetchUploadApiEndpoint("create-multipart-upload", {
          file,
          contentType: file.type,
          uploadType,
        }),
      listParts: (file, props) =>
        fetchUploadApiEndpoint("list-parts", {
          file,
          ...props,
        }),
      signPart: (file, props) =>
        fetchUploadApiEndpoint("sign-part", {
          file,
          ...props,
        }),
      abortMultipartUpload: (file, props) =>
        fetchUploadApiEndpoint("abort-multipart-upload", {
          file,
          ...props,
        }),
      completeMultipartUpload: (file, props) =>
        fetchUploadApiEndpoint("complete-multipart-upload", {
          file,
          ...props,
        }),
    });

    if (shouldUseImageEditor) {
      instance.use(ImageEditor);
      instance.use(Compressor, { mimeType: "image/*" });
    }

    return instance;
  }, [
    fileNamePrefix,
    restrictions,
    shouldUseImageEditor,
    uploadType,
    uppyLocale,
  ]);

  useEffect(() => {
    if (!aspectRatio || !shouldUseImageEditor) return;

    const onFileEditorComplete = (updatedFile: UppyFile<Metadata, AwsBody>) => {
      updatedFile.meta.aspectRatioApplied = true;
    };

    const enforceCrop = (files: Record<string, UppyFile<Metadata, AwsBody>>) =>
      Object.values(files).every((file) => {
        if (file.meta.aspectRatioApplied) return true;

        uppy.info({ message: t("cropRequired") }, "warning");
        uppy
          .getPlugin<DashboardPlugin<Metadata, AwsBody>>("DashboardModal")
          ?.openFileEditor(file);
        return false;
      });

    uppy.on("file-editor:complete", onFileEditorComplete);
    uppy.setOptions({ onBeforeUpload: enforceCrop });
    uppy
      .getPlugin("ImageEditor")
      ?.setOptions({ cropperOptions: { aspectRatio } });

    return () => {
      uppy.off("file-editor:complete", onFileEditorComplete);
      uppy.setOptions({ onBeforeUpload: undefined });
      uppy
        .getPlugin("ImageEditor")
        ?.setOptions({ cropperOptions: { aspectRatio: 0 } });
    };
  }, [aspectRatio, shouldUseImageEditor, uppy, t]);

  useEffect(() => {
    if (!onUploadSuccess) return;
    uppy.on("complete", onUploadSuccess);
    return () => {
      uppy.off("complete", onUploadSuccess);
    };
  }, [onUploadSuccess, uppy]);

  useEffect(() => {
    const handleUploadSuccess = (
      file: UppyFile<Metadata, AwsBody> | undefined,
      response: {
        body?: AwsBody;
        status: number;
        bytesUploaded?: number;
        uploadURL?: string;
      },
    ) => {
      if (!file) return;

      const uploadUrl = response.body?.location ?? response.uploadURL;

      if (uploadUrl) {
        setCurrentUrl(uploadUrl);
      }

      uppy.setFileState(file.id, {
        progress: uppy.getState().files[file.id].progress,
        uploadURL: uploadUrl,
        response,
        isPaused: false,
      });
    };

    uppy.on("upload-success", handleUploadSuccess);
    return () => {
      uppy.off("upload-success", handleUploadSuccess);
    };
  }, [uppy]);

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMounted(true);
  }, []);

  const openUploadModal = () => setIsOpen(true);
  const clearFile = () => {
    uppy.cancelAll();
    setCurrentUrl(undefined);
  };

  return (
    <>
      {typeof children === "function"
        ? children({
            currentUrl,
            openUploadModal,
            clearFile,
          })
        : children}

      {mounted &&
        createPortal(
          <DashboardModal
            open={isOpen}
            onRequestClose={() => setIsOpen(false)}
            showLinkToFileUploadResult={true}
            uppy={uppy}
            autoOpen={shouldUseImageEditor ? "imageEditor" : undefined}
            proudlyDisplayPoweredByUppy={false}
            closeModalOnClickOutside={true}
            closeAfterFinish={true}
            theme={theme.resolvedTheme === "dark" ? "dark" : "light"}
          />,
          document.body,
        )}
    </>
  );
}
